/**
 * ResearchScreen
 * Personal Research Analyst (PRA) main screen
 *
 * Features:
 * - Autonomous research pipeline
 * - Real-time step progress
 * - Final report display
 * - Agent flow visualization
 */

import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  ScrollView,
  TouchableOpacity,
  Alert,
  Modal,
  Pressable,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Colors } from '@/constants/colors';
import { useAuthStore } from '@/store/authStore';
import { researchAgent } from '@services/research/researchAgent';
import {
  ResearchEvent,
  AgentStep,
  AgentStepType,
  ResearchQuestion,
  ResearchSource,
} from '@/types/research.types';

// Components
import { ResearchInput } from '@components/research/ResearchInput';
import { StepProgress } from '@components/research/StepProgress';
import { ResearchReport } from '@components/research/ResearchReport';
import { FlowDiagram } from '@components/research/FlowDiagram';
import { SourceCard } from '@components/research/SourceCard';
import { useNavigation } from '@react-navigation/native';
import { MarkdownText } from '@components/common/MarkdownText';
import { ScrollColumn } from '@components/common/ScrollColumn';

type ViewMode = 'input' | 'processing' | 'complete';

export const ResearchScreen: React.FC = () => {
  const navigation = useNavigation();
  const { user } = useAuthStore();
  const [viewMode, setViewMode] = useState<ViewMode>('input');
  const [topic, setTopic] = useState('');
  const [isResearching, setIsResearching] = useState(false);
  const [researchStartTime, setResearchStartTime] = useState<number | null>(null);

  // Research data
  const [steps, setSteps] = useState<AgentStep[]>([]);
  const [questions, setQuestions] = useState<ResearchQuestion[]>([]);
  const [sources, setSources] = useState<ResearchSource[]>([]);
  const [finalReport, setFinalReport] = useState('');
  const [metadata, setMetadata] = useState<any>(null);

  // UI state
  const [selectedTab, setSelectedTab] = useState<'report' | 'sources' | 'flow'>('report');
  const [expandedStep, setExpandedStep] = useState<AgentStepType | null>(null);
  const [outputModal, setOutputModal] = useState<{
    visible: boolean;
    title: string;
    content: string;
  }>({ visible: false, title: '', content: '' });
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [compareModal, setCompareModal] = useState<{
    visible: boolean;
    draft: string;
    final: string;
  }>({ visible: false, draft: '', final: '' });
  const [compareFeedbackOpen, setCompareFeedbackOpen] = useState<boolean>(false);

  const openModal = (title: string, content: string) =>
    setOutputModal({ visible: true, title, content });
  const closeModal = () => setOutputModal({ visible: false, title: '', content: '' });
  const openCompare = (draft: string, finalText: string) =>
    setCompareModal({ visible: true, draft, final: finalText });
  const closeCompare = () => setCompareModal({ visible: false, draft: '', final: '' });

  /**
   * Start research process
   */
  const handleStartResearch = async (researchTopic: string) => {
    if (!user?.id) {
      Alert.alert('Error', 'You must be logged in to use the research assistant');
      return;
    }

    setTopic(researchTopic);
    setViewMode('processing');
    setIsResearching(true);
    setResearchStartTime(Date.now());

    // Initialize steps
    const initialSteps: AgentStep[] = [
      { type: 'planning', status: 'pending' },
      { type: 'searching', status: 'pending' },
      { type: 'extracting', status: 'pending' },
      { type: 'drafting', status: 'pending' },
      { type: 'critiquing', status: 'pending' },
      { type: 'refining', status: 'pending' },
    ];
    setSteps(initialSteps);

    try {
      // Start research agent (async generator)
      const researchGenerator = researchAgent.research(researchTopic, user.id);

      // Process events as they come in
      for await (const event of researchGenerator) {
        handleResearchEvent(event);
      }

      // Research complete
      setIsResearching(false);
    } catch (error) {
      console.error('[ResearchScreen] Research error:', error);
      Alert.alert('Research Failed', 'An error occurred during research. Please try again.');
      setIsResearching(false);
      setViewMode('input');
    }
  };

  /**
   * Handle real-time research events
   */
  const handleResearchEvent = (event: ResearchEvent) => {
    // Handle step error: stop and revert to input
    if (event.status === 'error') {
      setSteps(prev =>
        prev.map(step =>
          step.type === event.type
            ? {
                ...step,
                status: event.status,
                data: { ...(step.data || {}), ...(event.data || {}) },
                error: event.error,
              }
            : step
        )
      );
      setIsResearching(false);
      Alert.alert('Research Failed', event.error || 'An error occurred during research.');
      setViewMode('input');
      return;
    }

    // Update step status
    setSteps(prev =>
      prev.map(step =>
        step.type === event.type
          ? {
              ...step,
              status: event.status,
              data: { ...(step.data || {}), ...(event.data || {}) },
              error: event.error,
            }
          : step
      )
    );

    // Store step-specific data
    if (event.type === 'planning' && event.status === 'in_progress' && event.data?.sessionId) {
      setSessionId(event.data.sessionId);
    }
    if (event.status === 'complete' && event.data) {
      switch (event.type) {
        case 'planning':
          setQuestions(event.data.questions || []);
          break;
        case 'searching':
          setSources(event.data.sources || []);
          break;
        case 'extracting':
          setSources(event.data.sources || []);
          break;
        case 'drafting':
          // Preview draft (optional)
          break;
        case 'refining':
          setFinalReport(event.data.finalReport || event.data.draft || '');
          // Calculate metadata
          const duration =
            researchStartTime != null ? (Date.now() - researchStartTime) / 1000 : 0;
          setMetadata({
            cost: 0.06, // Estimated
            duration,
            questionsCount: questions.length,
            sourcesCount: sources.length,
          });
          // Mark complete on successful refinement
          setIsResearching(false);
          setViewMode('complete');
          break;
      }
    }
  };

  /**
   * Reset and start new research
   */
  const handleNewResearch = () => {
    setViewMode('input');
    setTopic('');
    setSteps([]);
    setQuestions([]);
    setSources([]);
    setFinalReport('');
    setMetadata(null);
    setSelectedTab('report');
    setExpandedStep(null);
  };

  /**
   * Get step labels for UI
   */
  const getStepLabel = (type: AgentStepType): string => {
    const labels: Record<AgentStepType, string> = {
      planning: 'Planning Research',
      searching: 'Searching Sources',
      extracting: 'Extracting Insights',
      drafting: 'Drafting Report',
      critiquing: 'Reviewing Quality',
      refining: 'Finalizing Report',
    };
    return labels[type];
  };

  /**
   * Render content based on view mode
   */
  const renderContent = () => {
    if (viewMode === 'input') {
      return (
        <View style={styles.inputContainer}>
          <View style={styles.header}>
            <Ionicons name="flask" size={40} color={Colors.accent.primary} />
            <Text style={styles.title}>Personal Research Analyst</Text>
            <Text style={styles.subtitle}>
              Autonomous AI agent that conducts deep research on any topic
            </Text>
            <TouchableOpacity
              style={styles.historyButton}
              onPress={() => navigation.navigate('ResearchHistory' as never)}
            >
              <Ionicons name="time" size={16} color={Colors.accent.primary} />
              <Text style={styles.historyText}>History</Text>
            </TouchableOpacity>
          </View>
          <ResearchInput onSubmit={handleStartResearch} isLoading={false} />

          <View style={styles.infoBox}>
            <Text style={styles.infoTitle}>How it works:</Text>
            <Text style={styles.infoText}>
              1. Agent generates focused research questions{'\n'}
              2. Searches the web for high-quality sources{'\n'}
              3. Extracts and summarizes key insights{'\n'}
              4. Drafts comprehensive report{'\n'}
              5. Reviews and refines for quality{'\n'}
              6. Delivers final research brief
            </Text>
            <Text style={styles.infoFooter}>
              Average time: 30-60 seconds • Cost: ~$0.06 per research
            </Text>
          </View>
        </View>
      );
    }

    if (viewMode === 'processing') {
      // Helpers to read current expanded step data
      const expandedData = steps.find(s => s.type === expandedStep)?.data || {};
      const eventDraftPreview =
        typeof expandedData.draft === 'string' ? expandedData.draft.slice(0, 600) : null;
      const eventCritique = expandedData.critique || null;
      const eventPartialExtracted: Array<{ title: string; summary: string; sourceName?: string }> =
        expandedData.partialExtracted || [];
      const draftCitations: string[] =
        (steps.find(s => s.type === 'drafting')?.data?.citations as string[]) || [];
      const refineCitations: string[] =
        (steps.find(s => s.type === 'refining')?.data?.citations as string[]) || [];

      const mapCitationsToSources = (citations: string[]) => {
        const getHost = (url: string) => {
          try {
            return new URL(url).hostname.replace('www.', '');
          } catch {
            return '';
          }
        };
        return citations.map(url => {
          const host = getHost(url);
          const match =
            sources.find(s => s.url === url) ||
            sources.find(s => getHost(s.url) === host);
          return { url, title: match?.title || url, sourceName: match?.sourceName || host };
        });
      };

      return (
        <View style={styles.processingContainer}>
          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
          <Text style={styles.processingTitle}>Researching: {topic}</Text>
            {!!sessionId && (
              <TouchableOpacity
                style={styles.refreshButton}
                onPress={async () => {
                  try {
                    if (!sessionId) return;
                    const { data } = await researchStorageService.getSession(sessionId);
                    if (data) {
                      if (data.questions) setQuestions(data.questions);
                      if (data.sources) setSources(data.sources);
                      if (data.finalReport) setFinalReport(data.finalReport);
                      if (data.metadata) setMetadata(data.metadata);
                      if (data.metadata?.stepDetails?.length) {
                        // Reconcile step statuses from persisted details
                        setSteps(prev =>
                          prev.map(s => {
                            const persisted = data.metadata!.stepDetails.find(d => d.type === s.type);
                            if (persisted) {
                              return {
                                ...s,
                                status: persisted.status,
                                startTime: persisted.startTime,
                                endTime: persisted.endTime,
                                cost: persisted.cost,
                                data: { ...(s.data || {}), ...(persisted as any).data },
                              };
                            }
                            return s;
                          })
                        );
                      }
                    }
                  } catch (e) {
                    console.error('[ResearchScreen] Refresh error:', e);
                  }
                }}
              >
                <Ionicons name="refresh" size={18} color={Colors.accent.primary} />
                <Text style={styles.refreshText}>Resume</Text>
              </TouchableOpacity>
            )}
          </View>
          <StepProgress
            steps={steps.map(step => ({
              type: step.type,
              status: step.status,
              label: getStepLabel(step.type),
              data: step.data,
            }))}
            onStepPress={type => setExpandedStep(expandedStep === type ? null : type)}
          />

          {/* Show expanded step details */}
          {expandedStep && (
            <View style={styles.stepDetails}>
              {expandedStep === 'planning' && questions.length > 0 && (
                <View>
                  <Text style={styles.stepDetailsTitle}>Research Questions</Text>
                  {questions.map((q, i) => (
                    <View key={i} style={styles.questionItem}>
                      <Text style={styles.questionText}>{q.question}</Text>
                      <Text style={styles.rationaleText}>{q.rationale}</Text>
                    </View>
                  ))}
                </View>
              )}

              {expandedStep === 'searching' && sources.length > 0 && (
                <View>
                  <Text style={styles.stepDetailsTitle}>Found {sources.length} Sources</Text>
                  <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                    {sources.slice(0, 5).map((source, i) => (
                      <View key={i} style={styles.sourceMini}>
                        <Text style={styles.sourceMiniTitle} numberOfLines={2}>
                          {source.title}
                        </Text>
                        <Text style={styles.sourceMiniName}>{source.sourceName}</Text>
                      </View>
                    ))}
                  </ScrollView>
                </View>
              )}

              {expandedStep === 'extracting' && (eventPartialExtracted.length > 0 || sources.length > 0) && (
                <View style={{ marginTop: 12 }}>
                  <Text style={styles.stepDetailsTitle}>Extracted Summaries</Text>
                  {(eventPartialExtracted.length > 0 ? eventPartialExtracted : sources).slice(0, 3).map((s, i) => (
                    <View key={i} style={{ marginBottom: 10 }}>
                      <Text style={styles.sourceMiniTitle}>{s.title}</Text>
                      <Text style={styles.rationaleText}>{s.summary}</Text>
                    </View>
                  ))}
                </View>
              )}

              {expandedStep === 'drafting' && (eventDraft || eventDraftPreview) && (
                <View style={{ marginTop: 12 }}>
                  <Text style={styles.stepDetailsTitle}>Draft Preview</Text>
                  <Text style={styles.rationaleText}>{eventDraft || eventDraftPreview}</Text>
                  <TouchableOpacity
                    style={{ marginTop: 8 }}
                    onPress={() => openModal('Draft', (eventDraft || eventDraftPreview || ''))}
                  >
                    <Text style={[styles.rationaleText, { color: Colors.accent.primary }]}>
                      View full
                    </Text>
                  </TouchableOpacity>
                  {!!draftCitations.length && (
                    <View style={{ marginTop: 10 }}>
                      <Text style={styles.stepDetailsTitle}>Sources used</Text>
                      {mapCitationsToSources(draftCitations).slice(0, 5).map((c, i) => (
                        <Text key={i} style={styles.rationaleText}>
                          • {c.title} {c.sourceName ? `(${c.sourceName})` : ''}
                        </Text>
                      ))}
                      {draftCitations.length > 5 && (
                        <TouchableOpacity
                          style={{ marginTop: 6 }}
                          onPress={() =>
                            openModal(
                              'All Draft Sources',
                              mapCitationsToSources(draftCitations)
                                .map(c => `• ${c.title}${c.sourceName ? ` (${c.sourceName})` : ''}\n${c.url}`)
                                .join('\n\n')
                            )
                          }
                        >
                          <Text style={[styles.rationaleText, { color: Colors.accent.primary }]}>
                            View all ({draftCitations.length})
                          </Text>
                        </TouchableOpacity>
                      )}
                    </View>
                  )}
                </View>
              )}

              {expandedStep === 'critiquing' && eventCritique && (
                <View style={{ marginTop: 12 }}>
                  <Text style={styles.stepDetailsTitle}>Critique</Text>
                  <Text style={styles.questionText}>Overall: {eventCritique.overallQuality}</Text>
                  {eventCritique.strengths?.length ? (
                    <View style={{ marginTop: 6 }}>
                      <Text style={styles.questionText}>Strengths</Text>
                      {eventCritique.strengths.map((t: string, i: number) => (
                        <Text key={i} style={styles.rationaleText}>
                          • {t}
                        </Text>
                      ))}
                    </View>
                  ) : null}
                  {eventCritique.weaknesses?.length ? (
                    <View style={{ marginTop: 6 }}>
                      <Text style={styles.questionText}>Weaknesses</Text>
                      {eventCritique.weaknesses.map((t: string, i: number) => (
                        <Text key={i} style={styles.rationaleText}>
                          • {t}
                        </Text>
                      ))}
                    </View>
                  ) : null}
                  {eventCritique.suggestions?.length ? (
                    <View style={{ marginTop: 6 }}>
                      <Text style={styles.questionText}>Suggestions</Text>
                      {eventCritique.suggestions.map((t: string, i: number) => (
                        <Text key={i} style={styles.rationaleText}>
                          • {t}
                        </Text>
                      ))}
                    </View>
                  ) : null}
                  {eventCritique.valueAdded?.length ? (
                    <View style={{ marginTop: 6 }}>
                      <Text style={styles.questionText}>Value Added</Text>
                      {eventCritique.valueAdded.map((t: string, i: number) => (
                        <Text key={i} style={styles.rationaleText}>
                          • {t}
                        </Text>
                      ))}
                    </View>
                  ) : null}
                </View>
              )}

              {expandedStep === 'refining' && finalReport && (
                <View style={{ marginTop: 12 }}>
                  <Text style={styles.stepDetailsTitle}>Final Report (Preview)</Text>
                  <Text numberOfLines={8} style={styles.rationaleText}>
                    {finalReport}
                  </Text>
                  <TouchableOpacity
                    style={{ marginTop: 8 }}
                    onPress={() => openModal('Final Report', finalReport)}
                  >
                    <Text style={[styles.rationaleText, { color: Colors.accent.primary }]}>
                      View full
                    </Text>
                  </TouchableOpacity>
                  {!!refineCitations.length && (
                    <View style={{ marginTop: 10 }}>
                      <Text style={styles.stepDetailsTitle}>Sources used</Text>
                      {mapCitationsToSources(refineCitations).slice(0, 5).map((c, i) => (
                        <Text key={i} style={styles.rationaleText}>
                          • {c.title} {c.sourceName ? `(${c.sourceName})` : ''}
                        </Text>
                      ))}
                      {refineCitations.length > 5 && (
                        <TouchableOpacity
                          style={{ marginTop: 6 }}
                          onPress={() =>
                            openModal(
                              'All Final Sources',
                              mapCitationsToSources(refineCitations)
                                .map(c => `• ${c.title}${c.sourceName ? ` (${c.sourceName})` : ''}\n${c.url}`)
                                .join('\n\n')
                            )
                          }
                        >
                          <Text style={[styles.rationaleText, { color: Colors.accent.primary }]}>
                            View all ({refineCitations.length})
                          </Text>
                        </TouchableOpacity>
                      )}
                    </View>
                  )}
                        {!!(steps.find(s => s.type === 'refining')?.data?.appliedValueAdded || []).length && (
                          <View style={{ marginTop: 10 }}>
                            <Text style={styles.stepDetailsTitle}>Value Added Applied</Text>
                            {(steps.find(s => s.type === 'refining')?.data?.appliedValueAdded as string[])
                              .slice(0, 6)
                              .map((t: string, i: number) => (
                                <Text key={i} style={styles.rationaleText}>
                                  • {t}
                                </Text>
                              ))}
                          </View>
                        )}
                </View>
              )}

              <View style={{ marginTop: 8 }}>
                <Text style={styles.stepDetailsTitle}>Progress</Text>
                <Text style={styles.progressLine}>
                  {steps.find(s => s.type === expandedStep)?.data?.progress ||
                    'Waiting for updates...'}
                </Text>
              </View>
            </View>
          )}
        </View>
      );
    }

    if (viewMode === 'complete') {
      return (
        <View style={styles.completeContainer}>
          <View style={styles.completeHeader}>
            <Ionicons name="checkmark-circle" size={32} color={Colors.status.success} />
            <Text style={styles.completeTitle}>Research Complete</Text>
            <Text style={styles.completeTopic}>{topic}</Text>
          </View>

          {/* Tabs */}
          <View style={styles.tabs}>
            <TouchableOpacity
              style={[styles.tab, selectedTab === 'report' && styles.tabActive]}
              onPress={() => setSelectedTab('report')}
            >
              <Text style={[styles.tabText, selectedTab === 'report' && styles.tabTextActive]}>
                Report
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.tab, selectedTab === 'sources' && styles.tabActive]}
              onPress={() => setSelectedTab('sources')}
            >
              <Text style={[styles.tabText, selectedTab === 'sources' && styles.tabTextActive]}>
                Sources ({sources.length})
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.tab, selectedTab === 'flow' && styles.tabActive]}
              onPress={() => setSelectedTab('flow')}
            >
              <Text style={[styles.tabText, selectedTab === 'flow' && styles.tabTextActive]}>
                Flow
              </Text>
            </TouchableOpacity>
          </View>

          {/* Tab content */}
          <View style={styles.tabContent}>
            {selectedTab === 'report' && (
              <ResearchReport report={finalReport} metadata={metadata} />
            )}
            {/* Compare Button */}
            <TouchableOpacity
              style={styles.compareButton}
              onPress={() =>
                openCompare(
                  (steps.find(s => s.type === 'drafting')?.data?.draft as string) ||
                    (steps.find(s => s.type === 'drafting')?.data?.draftFull as string) ||
                    '',
                  finalReport || ''
                )
              }
            >
              <Ionicons name="swap-horizontal" size={18} color={Colors.background.primary} />
              <Text style={styles.compareText}>Compare Draft vs Final</Text>
            </TouchableOpacity>
            {selectedTab === 'sources' && (
              <ScrollView showsVerticalScrollIndicator={false}>
                {sources.map((source, i) => (
                  <SourceCard key={i} source={source} index={i} />
                ))}
              </ScrollView>
            )}
            {selectedTab === 'flow' && (
              <>
                <FlowDiagram
                  steps={steps}
                  onStepPress={type => setExpandedStep(expandedStep === type ? null : type)}
                />
                {expandedStep && (
                  <View style={styles.stepDetails}>
                    {expandedStep === 'planning' && questions.length > 0 && (
                      <View>
                        <Text style={styles.stepDetailsTitle}>Research Questions</Text>
                        {questions.map((q, i) => (
                          <View key={i} style={styles.questionItem}>
                            <Text style={styles.questionText}>{q.question}</Text>
                            <Text style={styles.rationaleText}>{q.rationale}</Text>
                          </View>
                        ))}
                      </View>
                    )}
                    {expandedStep === 'searching' && sources.length > 0 && (
                      <View>
                        <Text style={styles.stepDetailsTitle}>Found {sources.length} Sources</Text>
                        <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                          {sources.slice(0, 5).map((source, i) => (
                            <View key={i} style={styles.sourceMini}>
                              <Text style={styles.sourceMiniTitle} numberOfLines={2}>
                                {source.title}
                              </Text>
                              <Text style={styles.sourceMiniName}>{source.sourceName}</Text>
                            </View>
                          ))}
                        </ScrollView>
                      </View>
                    )}
                    {expandedStep === 'extracting' && sources.length > 0 && (
                      <View style={{ marginTop: 12 }}>
                        <Text style={styles.stepDetailsTitle}>Extracted Summaries</Text>
                        {sources.slice(0, 3).map((s, i) => (
                          <View key={i} style={{ marginBottom: 10 }}>
                            <Text style={styles.sourceMiniTitle}>{s.title}</Text>
                            <Text style={styles.rationaleText}>{s.summary}</Text>
                          </View>
                        ))}
                        {sources.length > 3 && (
                          <TouchableOpacity
                            style={{ marginTop: 8 }}
                            onPress={() =>
                              openModal(
                                'All Extracted Summaries',
                                sources.map(s => `• ${s.title}\n${s.summary}`).join('\n\n')
                              )
                            }
                          >
                            <Text style={[styles.rationaleText, { color: Colors.accent.primary }]}>
                              View all ({sources.length})
                            </Text>
                          </TouchableOpacity>
                        )}
                      </View>
                    )}
                    {/* Use draft if present in step data; fall back to final report */}
                    {expandedStep === 'drafting' && (
                      <View style={{ marginTop: 12 }}>
                        <Text style={styles.stepDetailsTitle}>Draft</Text>
                        <Text numberOfLines={6} style={styles.rationaleText}>
                          {steps.find(s => s.type === 'drafting')?.data?.draft || finalReport}
                        </Text>
                        <TouchableOpacity
                          style={{ marginTop: 8 }}
                          onPress={() =>
                            openModal(
                              'Draft',
                              (steps.find(s => s.type === 'drafting')?.data?.draft as string) ||
                                finalReport ||
                                ''
                            )
                          }
                        >
                          <Text style={[styles.rationaleText, { color: Colors.accent.primary }]}>
                            View full
                          </Text>
                        </TouchableOpacity>
                        {!!(steps.find(s => s.type === 'drafting')?.data?.citations || []).length && (
                          <View style={{ marginTop: 10 }}>
                            <Text style={styles.stepDetailsTitle}>Sources used</Text>
                            {(() => {
                              const citations = (steps.find(s => s.type === 'drafting')?.data
                                ?.citations || []) as string[];
                              const getHost = (url: string) => {
                                try {
                                  return new URL(url).hostname.replace('www.', '');
                                } catch {
                                  return '';
                                }
                              };
                              const mapped = citations.map(url => {
                                const host = getHost(url);
                                const match =
                                  sources.find(s => s.url === url) ||
                                  sources.find(s => getHost(s.url) === host);
                                return {
                                  url,
                                  title: match?.title || url,
                                  sourceName: match?.sourceName || host,
                                };
                              });
                              return mapped.slice(0, 5).map((c, i) => (
                                <Text key={i} style={styles.rationaleText}>
                                  • {c.title} {c.sourceName ? `(${c.sourceName})` : ''}
                                </Text>
                              ));
                            })()}
                          </View>
                        )}
                      </View>
                    )}
                    {expandedStep === 'critiquing' && steps.find(s => s.type === 'critiquing')?.data?.critique && (
                      <View style={{ marginTop: 12 }}>
                        <Text style={styles.stepDetailsTitle}>Critique</Text>
                        <Text style={styles.questionText}>
                          Overall: {steps.find(s => s.type === 'critiquing')?.data?.critique?.overallQuality}
                        </Text>
                        {(steps.find(s => s.type === 'critiquing')?.data?.critique?.strengths || []).slice(0, 5).map((t: string, i: number) => (
                          <Text key={i} style={styles.rationaleText}>• {t}</Text>
                        ))}
                        {(steps.find(s => s.type === 'critiquing')?.data?.critique?.weaknesses || []).slice(0, 5).map((t: string, i: number) => (
                          <Text key={i} style={styles.rationaleText}>• {t}</Text>
                        ))}
                        {(steps.find(s => s.type === 'critiquing')?.data?.critique?.suggestions || []).slice(0, 5).map((t: string, i: number) => (
                          <Text key={i} style={styles.rationaleText}>• {t}</Text>
                        ))}
                      </View>
                    )}
                    {expandedStep === 'refining' && finalReport && (
                      <View style={{ marginTop: 12 }}>
                        <Text style={styles.stepDetailsTitle}>Final Report (Preview)</Text>
                        <Text numberOfLines={8} style={styles.rationaleText}>
                          {finalReport}
                        </Text>
                        <TouchableOpacity
                          style={{ marginTop: 8 }}
                          onPress={() => openModal('Final Report', finalReport)}
                        >
                          <Text style={[styles.rationaleText, { color: Colors.accent.primary }]}>
                            View full
                          </Text>
                        </TouchableOpacity>
                        {/* Show which suggestions were applied (from step details metadata) */}
                        {!!steps.find(s => s.type === 'refining')?.data?.appliedSuggestions?.length && (
                          <View style={{ marginTop: 10 }}>
                            <Text style={styles.stepDetailsTitle}>Applied Feedback</Text>
                            {steps
                              .find(s => s.type === 'refining')
                              ?.data?.appliedSuggestions?.slice(0, 5)
                              .map((t: string, i: number) => (
                                <Text key={i} style={styles.rationaleText}>• {t}</Text>
                              ))}
                          </View>
                        )}
                      </View>
                    )}
                  </View>
                )}
              </>
            )}
          </View>

          {/* Actions */}
          <TouchableOpacity style={styles.newResearchButton} onPress={handleNewResearch}>
            <Ionicons name="add-circle" size={20} color={Colors.background.primary} />
            <Text style={styles.newResearchText}>New Research</Text>
          </TouchableOpacity>
        </View>
      );
    }

    return null;
  };

  return (
    <SafeAreaView style={styles.container}>
      <ScrollView contentContainerStyle={styles.scrollContent} showsVerticalScrollIndicator={false}>
        {renderContent()}
      </ScrollView>
      <Modal visible={outputModal.visible} animationType="slide" onRequestClose={closeModal} transparent={false}>
        <SafeAreaView style={styles.modalContainer}>
          <View style={styles.modalHeader}>
            <Pressable onPress={closeModal} style={styles.modalClose}>
              <Ionicons name="close" size={24} color={Colors.text.primary} />
            </Pressable>
            <Text style={styles.modalTitle} numberOfLines={1}>
              {outputModal.title}
            </Text>
            <View style={{ width: 24 }} />
          </View>
          <ScrollView contentContainerStyle={styles.modalContent}>
            <MarkdownText text={outputModal.content} />
          </ScrollView>
        </SafeAreaView>
      </Modal>
      <Modal visible={compareModal.visible} animationType="slide" onRequestClose={closeCompare} transparent={false}>
        <SafeAreaView style={styles.modalContainer}>
          <View style={styles.modalHeader}>
            <Pressable onPress={closeCompare} style={styles.modalClose}>
              <Ionicons name="close" size={24} color={Colors.text.primary} />
            </Pressable>
            <Text style={styles.modalTitle} numberOfLines={1}>
              Draft vs Final
            </Text>
            <View style={{ width: 24 }} />
          </View>
          {(() => {
            const critique = (steps.find(s => s.type === 'critiquing')?.data as any)?.critique || null;
            const appliedSuggestions =
              ((steps.find(s => s.type === 'refining')?.data as any)?.appliedSuggestions as string[]) || [];
            const appliedValueAdded =
              ((steps.find(s => s.type === 'refining')?.data as any)?.appliedValueAdded as string[]) || [];
            const hasFeedback =
              !!critique ||
              (appliedSuggestions && appliedSuggestions.length > 0) ||
              (appliedValueAdded && appliedValueAdded.length > 0);
            if (!hasFeedback) return null;
            return (
              <View style={styles.feedbackHeader}>
                <TouchableOpacity
                  style={styles.feedbackHeaderButton}
                  onPress={() => setCompareFeedbackOpen(prev => !prev)}
                  activeOpacity={0.7}
                >
                  <Ionicons
                    name={compareFeedbackOpen ? 'chevron-up' : 'chevron-down'}
                    size={16}
                    color={Colors.text.primary}
                  />
                  <Text style={styles.feedbackHeaderText}>Feedback & Changes</Text>
                </TouchableOpacity>
                {compareFeedbackOpen && (
                  <ScrollView contentContainerStyle={styles.modalContent}>
                    {critique?.overallQuality && (
                      <View style={styles.badgeRow}>
                        <View style={styles.badge}>
                          <Ionicons name="ribbon" size={14} color={Colors.accent.primary} />
                          <Text style={styles.badgeText}>Overall: {critique.overallQuality}</Text>
                        </View>
                      </View>
                    )}
                    {!!(critique?.strengths || []).length && (
                      <View style={{ marginTop: 6 }}>
                        <Text style={styles.feedbackSectionTitle}>Strengths</Text>
                        {(critique.strengths as string[]).map((t, i) => (
                          <Text key={i} style={styles.modalText}>• {t}</Text>
                        ))}
                      </View>
                    )}
                    {!!(critique?.weaknesses || []).length && (
                      <View style={{ marginTop: 6 }}>
                        <Text style={styles.feedbackSectionTitle}>Weaknesses</Text>
                        {(critique.weaknesses as string[]).map((t, i) => (
                          <Text key={i} style={styles.modalText}>• {t}</Text>
                        ))}
                      </View>
                    )}
                    {!!(critique?.suggestions || []).length && (
                      <View style={{ marginTop: 6 }}>
                        <Text style={styles.feedbackSectionTitle}>Suggestions</Text>
                        {(critique.suggestions as string[]).map((t, i) => {
                          const applied =
                            (appliedSuggestions || []).some(s => s.toLowerCase() === t.toLowerCase());
                          return (
                            <View key={i} style={styles.changeRow}>
                              <Ionicons
                                name={applied ? 'checkmark-circle' : 'ellipse-outline'}
                                size={16}
                                color={applied ? Colors.status.success : Colors.text.tertiary}
                              />
                              <Text style={styles.modalText}>{t}</Text>
                            </View>
                          );
                        })}
                      </View>
                    )}
                    {!!(critique?.valueAdded || []).length && (
                      <View style={{ marginTop: 6 }}>
                        <Text style={styles.feedbackSectionTitle}>Value Added</Text>
                        {(critique.valueAdded as string[]).map((t, i) => {
                          const applied =
                            (appliedValueAdded || []).some(s => s.toLowerCase() === t.toLowerCase());
                          return (
                            <View key={i} style={styles.changeRow}>
                              <Ionicons
                                name={applied ? 'checkmark-circle' : 'ellipse-outline'}
                                size={16}
                                color={applied ? Colors.status.success : Colors.text.tertiary}
                              />
                              <Text style={styles.modalText}>{t}</Text>
                            </View>
                          );
                        })}
                      </View>
                    )}
                  </ScrollView>
                )}
              </View>
            );
          })()}
          <View style={styles.modalCompareRow}>
            <View style={styles.modalColumn}>
              <Text style={styles.modalColumnTitle}>Draft</Text>
              <ScrollColumn contentContainerStyle={styles.modalColumnContent}>
                <MarkdownText text={compareModal.draft || '—'} />
              </ScrollColumn>
            </View>
            <View style={styles.modalColumn}>
              <Text style={styles.modalColumnTitle}>Final</Text>
              <ScrollColumn contentContainerStyle={styles.modalColumnContent}>
                <MarkdownText text={compareModal.final || '—'} />
              </ScrollColumn>
            </View>
          </View>
        </SafeAreaView>
      </Modal>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.background.primary,
  },
  scrollContent: {
    padding: 16,
  },

  // Input view
  inputContainer: {},
  header: {
    alignItems: 'center',
    marginBottom: 24,
  },
  historyButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginTop: 8,
    backgroundColor: Colors.background.secondary,
    borderWidth: 1,
    borderColor: Colors.border.subtle,
    borderRadius: 8,
    paddingHorizontal: 10,
    paddingVertical: 6,
  },
  historyText: {
    fontSize: 13,
    fontWeight: '600',
    color: Colors.accent.primary,
  },
  title: {
    fontSize: 24,
    fontWeight: '700',
    color: Colors.text.primary,
    marginTop: 12,
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 15,
    color: Colors.text.secondary,
    textAlign: 'center',
    paddingHorizontal: 20,
  },
  infoBox: {
    backgroundColor: Colors.background.secondary,
    borderRadius: 12,
    padding: 16,
    marginTop: 16,
  },
  infoTitle: {
    fontSize: 15,
    fontWeight: '600',
    color: Colors.text.primary,
    marginBottom: 8,
  },
  infoText: {
    fontSize: 14,
    color: Colors.text.secondary,
    lineHeight: 22,
  },
  infoFooter: {
    fontSize: 12,
    color: Colors.text.tertiary,
    marginTop: 12,
    fontStyle: 'italic',
  },

  // Processing view
  processingContainer: {},
  processingTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: Colors.text.primary,
    marginBottom: 16,
  },
  stepDetails: {
    backgroundColor: Colors.background.secondary,
    borderRadius: 12,
    padding: 16,
    marginTop: 16,
  },
  stepDetailsTitle: {
    fontSize: 15,
    fontWeight: '600',
    color: Colors.text.primary,
    marginBottom: 12,
  },
  questionItem: {
    marginBottom: 12,
  },
  questionText: {
    fontSize: 14,
    fontWeight: '600',
    color: Colors.text.primary,
    marginBottom: 4,
  },
  rationaleText: {
    fontSize: 13,
    color: Colors.text.secondary,
  },
  sourceMini: {
    width: 180,
    backgroundColor: Colors.background.primary,
    borderRadius: 8,
    padding: 12,
    marginRight: 12,
    borderWidth: 1,
    borderColor: Colors.border.subtle,
  },
  sourceMiniTitle: {
    fontSize: 13,
    fontWeight: '600',
    color: Colors.text.primary,
    marginBottom: 6,
  },
  sourceMiniName: {
    fontSize: 11,
    color: Colors.text.tertiary,
  },

  progressLine: {
    fontSize: 13,
    color: Colors.text.secondary,
  },

  // Complete view
  completeContainer: {
    flex: 1,
  },
  completeHeader: {
    alignItems: 'center',
    marginBottom: 20,
  },
  completeTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: Colors.text.primary,
    marginTop: 8,
  },
  completeTopic: {
    fontSize: 14,
    color: Colors.text.secondary,
    marginTop: 4,
  },
  tabs: {
    flexDirection: 'row',
    backgroundColor: Colors.background.secondary,
    borderRadius: 12,
    padding: 4,
    marginBottom: 16,
  },
  tab: {
    flex: 1,
    paddingVertical: 10,
    alignItems: 'center',
    borderRadius: 8,
  },
  tabActive: {
    backgroundColor: Colors.accent.primary,
  },
  tabText: {
    fontSize: 14,
    fontWeight: '600',
    color: Colors.text.secondary,
  },
  tabTextActive: {
    color: Colors.background.primary,
  },
  tabContent: {
    flex: 1,
  },
  compareButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: Colors.accent.primary,
    borderRadius: 10,
    paddingVertical: 10,
    marginBottom: 12,
    gap: 8,
  },
  compareText: {
    fontSize: 14,
    fontWeight: '700',
    color: Colors.background.primary,
  },
  newResearchButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: Colors.accent.primary,
    borderRadius: 12,
    padding: 16,
    marginTop: 16,
  },
  newResearchText: {
    fontSize: 16,
    fontWeight: '600',
    color: Colors.background.primary,
    marginLeft: 8,
  },
  // Modal
  modalContainer: {
    flex: 1,
    backgroundColor: Colors.background.primary,
  },
  modalHeader: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    flexDirection: 'row',
    alignItems: 'center',
  },
  modalClose: {
    padding: 8,
    marginRight: 8,
  },
  modalTitle: {
    flex: 1,
    fontSize: 16,
    fontWeight: '700',
    color: Colors.text.primary,
    textAlign: 'center',
  },
  modalContent: {
    padding: 16,
  },
  modalText: {
    fontSize: 15,
    lineHeight: 22,
    color: Colors.text.secondary,
  },
  modalCompareRow: {
    flex: 1,
    flexDirection: 'row',
    gap: 12,
    paddingHorizontal: 16,
    paddingBottom: 16,
  },
  modalColumn: {
    flex: 1,
    backgroundColor: Colors.background.secondary,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: Colors.border.subtle,
    overflow: 'hidden',
  },
  modalColumnTitle: {
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 14,
    fontWeight: '700',
    color: Colors.text.primary,
    borderBottomWidth: 1,
    borderBottomColor: Colors.border.subtle,
  },
  modalColumnContent: {
    padding: 12,
  },
  feedbackTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: Colors.text.primary,
    marginBottom: 6,
  },
  feedbackSectionTitle: {
    fontSize: 14,
    fontWeight: '700',
    color: Colors.text.primary,
    marginBottom: 4,
  },
  badgeRow: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 4,
  },
  feedbackHeader: {
    paddingHorizontal: 16,
    paddingBottom: 8,
  },
  feedbackHeaderButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: Colors.border.subtle,
    backgroundColor: Colors.background.secondary,
    alignSelf: 'flex-start',
  },
  feedbackHeaderText: {
    fontSize: 12,
    fontWeight: '700',
    color: Colors.text.primary,
  },
  badge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 999,
    backgroundColor: Colors.background.secondary,
    borderWidth: 1,
    borderColor: Colors.border.subtle,
    alignSelf: 'flex-start',
  },
  badgeText: {
    fontSize: 12,
    color: Colors.text.secondary,
    fontWeight: '700',
  },
  changeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 6,
  },
  refreshButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: Colors.accent.primary,
    backgroundColor: Colors.background.secondary,
  },
  refreshText: {
    fontSize: 12,
    fontWeight: '700',
    color: Colors.accent.primary,
  },
});
